import { formatDistanceStrict } from 'date-fns';

import { EMPTY_PLACEHOLDER } from './constants';
import { toDateOrNull } from './parse';
import type { DateInput } from './types';

export interface RelativeTimeOptions {
  /**
   * The instant to measure against. Defaults to now.
   *
   * Injectable on purpose: "5 minutes ago" is otherwise unassertable, and an
   * assertion that cannot be written is an assertion nobody writes.
   */
  baseDate?: Date;
  /** Rendered when the input is not a date. */
  fallback?: string;
}

/**
 * Formats a timestamp as elapsed time — "3 hours ago", "in 2 days".
 *
 * Deliberately `formatDistanceStrict(target, base)` and NOT
 * `formatDistanceToNowStrict(target, { baseDate })`. The `…ToNow` helpers read
 * the system clock internally and silently IGNORE a `baseDate` option, so the
 * apparently-deterministic version returns a different answer on every run.
 * The base date has to be passed positionally for it to take effect at all.
 *
 * Direction is carried by argument ORDER: date-fns renders "ago" when the
 * first argument precedes the second, and "in" when it follows. Swapping them
 * would print a seven-month-old record as "in 7 months" — the worst possible
 * failure on a log or audit surface.
 *
 * Strict rather than approximate. "about 1 hour ago" beside a live-refreshing
 * list is text that changes without the data changing, which erodes trust in
 * every other timestamp on screen.
 *
 * NOT MONEY, and deliberately never extended to amounts.
 */
export function formatRelativeTime(value: DateInput, options: RelativeTimeOptions = {}): string {
  const { baseDate, fallback = EMPTY_PLACEHOLDER } = options;
  const date = toDateOrNull(value);
  if (date === null) return fallback;

  return formatDistanceStrict(date, baseDate ?? new Date(), { addSuffix: true });
}
